import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Map, List, fromJS } from 'immutable'
import { Modifier, EditorState, SelectionState } from 'draft-js'

// suggestions plugin from rdf-ui
import createSuggestionsPlugin from '@aytm/lib/rfd-ui/components/advanced/lib/draft-js/suggestionsPlugin'
import ErrorsDecoratorFactory from '@aytm/lib/rfd-ui/components/advanced/lib/draft-js/decorators/ErrorsDecoratorFactory'

// suggestions plugin from stats
import extractAytmLogic from '@aytm/app/stats/lib/draft-js/autocompletePlugin/lib/extract-aytm-logic'
import extractSelection from '@aytm/app/stats/lib/draft-js/autocompletePlugin/lib/extract-selection'
import extractParserResult from '@aytm/app/stats/lib/draft-js/autocompletePlugin/lib/extract-parser-result'
import suggestionsBuilders from '@aytm/app/stats/lib/draft-js/autocompletePlugin/builders'
import SuggestionsComponent from '@aytm/app/stats/lib/draft-js/autocompletePlugin/components/Suggestions'

import ErrorsDecoratorStrategy from '@aytm/app/stats/lib/draft-js/decorators/errors-decorator-strategy'
import WarningsDecoratorStrategy from '@aytm/app/stats/lib/draft-js/decorators/warnings-decorator-strategy'

// components
import RichText from '@aytm/lib/rfd-ui/components/advanced/rich-text'

// styles
import '@aytm/app/stats/page/styles/globals.scss'

import style from '@aytm/app/stats/page/components/styles/AdvancedFilter.module.scss'

const STORAGE_KEY = 'stats-autocomplete'
const EVENT_KEY = 'statsKeydownEvent'
const HOTKEYS = 'CTRL + ['

const toggleAutocomplete = () => {
  window.localStorage.setItem(STORAGE_KEY, window.localStorage.getItem(STORAGE_KEY) !== 'true')
  const event = document.createEvent('Event')
  event.initEvent(EVENT_KEY, true, true)
  dispatchEvent(event)
}

window.addEventListener('keydown', event => {
  if (event.ctrlKey && '[' === event.key) {
    toggleAutocomplete()
  }
})

export default class AdvancedFilter extends React.Component {
  static propTypes = {
    locale: PropTypes.instanceOf(Map).isRequired,
    parserResultEof: PropTypes.object.isRequired,
    parserResultPattern: PropTypes.object.isRequired,
    surveySchema: PropTypes.object.isRequired,
    hintPosition: PropTypes.oneOf(['top', 'bottom']),
    pattern: PropTypes.string.isRequired,
    prefix: PropTypes.string,
    disabled: PropTypes.bool,
    onChange: PropTypes.func,
    onUpdate: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onActive: PropTypes.func
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const { parserResultPattern, pattern } = nextProps
    let newState = null

    if (pattern !== prevState.pattern) {
      newState = { pattern }
    }

    if (parserResultPattern !== prevState.parserResultPattern) {
      const parserState = {
        parserResultPattern,
        errors: fromJS(parserResultPattern.expression.status.errors)
      }
      newState = newState ? Object.assign({}, newState, parserState) : parserState
    }

    return newState
  }

  constructor(props) {
    super(props)
    this.createDecorators = this.createDecorators.bind(this)
    this.createPlugins = this.createPlugins.bind(this)
    this.canAutocompleteBeActive = this.canAutocompleteBeActive.bind(this)
    this.onAutocompleteUpdate = this.onAutocompleteUpdate.bind(this)
    this.onMouseEnter = this.onMouseEnter.bind(this)
    this.onMouseLeave = this.onMouseLeave.bind(this)
    this.onEditorChange = this.onEditorChange.bind(this)
    this.onEditorUpdate = this.onEditorUpdate.bind(this)
    this.onEditorFocus = this.onEditorFocus.bind(this)
    this.onEditorBlur = this.onEditorBlur.bind(this)
    this.handlePastedText = this.handlePastedText.bind(this)
    this.onSuggestionsBeforeSelect = this.onSuggestionsBeforeSelect.bind(this)
    this.setActiveState = this.setActiveState.bind(this)
    this.updateDecoratorErrors = this.updateDecoratorErrors.bind(this)
    this.updateSuggestionsComponent = this.updateSuggestionsComponent.bind(this)
    this.calculateCursorStyles = this.calculateCursorStyles.bind(this)
    this.getSuggestionPlugin = this.getSuggestionPlugin.bind(this)
    this.renderSuggestions = this.renderSuggestions.bind(this)
    this.renderHint = this.renderHint.bind(this)
    const { parserResultEof, parserResultPattern, surveySchema, locale } = props

    this.state = {
      parserResultEof,
      surveySchema,
      active: false,
      hovered: false,
      cursorTextStyle: null,
      errors: List(),
      showHint: false
    }

    this.parserTimer = null

    this.refSuggestionsComponent = null

    this.pluginStore = {
      canAutocompleteBeActive: this.canAutocompleteBeActive,
      getSuggestionsData: (beforeCursor, afterCursor) => {
        return {
          aytmLogic: extractAytmLogic(surveySchema),
          parserResult: extractParserResult(beforeCursor, afterCursor, parserResultPattern, parserResultEof)
        }
      }
    }

    this.decoratorErrorsStore = {
      strategy: ErrorsDecoratorStrategy,
      getLocale: () => locale,
      getErrors: () => this.state.errors
    }

    this.plugins = this.createPlugins()
    this.decorators = this.createDecorators()
  }

  createDecorators() {
    return [ErrorsDecoratorFactory(this.decoratorErrorsStore)]
  }

  createPlugins() {
    return [
      createSuggestionsPlugin(this.pluginStore, {
        extractSelection,
        suggestionsBuilders,
        SuggestionsComponent
      })
    ]
  }

  canAutocompleteBeActive() {
    return true !== this.props.disabled && 'false' !== window.localStorage.getItem(STORAGE_KEY)
  }

  onAutocompleteUpdate() {
    this.forceUpdate(() => {
      this.updateSuggestionsComponent()
    })
  }

  onMouseEnter() {
    const { active, showHint } = this.state
    const { hintPosition } = this.props
    if (!showHint) {
      this.setState({
        showHint: 'top' === hintPosition || !active,
        hovered: true
      })
    } else {
      this.setState({
        hovered: true
      })
    }
  }

  onMouseLeave() {
    const { active, showHint } = this.state
    if (!active && showHint) {
      this.setState({
        showHint: false,
        hovered: false
      })
    } else {
      this.setState({
        hovered: false
      })
    }
  }

  onEditorChange(getContent) {
    const { onChange } = this.props
    const { html } = getContent({ text: false, html: true })

    setTimeout(() => {
      this.pluginStore.canAutocompleteBeActive = this.canAutocompleteBeActive

      this.updateSuggestionsComponent()
    })

    'function' == typeof onChange && onChange(html)
  }

  onEditorUpdate(editorState, prevEditorState) {
    this.pluginStore.canAutocompleteBeActive = this.canAutocompleteBeActive

    const { onUpdate } = this.props
    const nextPattern = editorState.getCurrentContent().getPlainText()

    if (nextPattern != this.state.pattern) {
      this.pluginStore.canAutocompleteBeActive = () => false
    } else {
      setTimeout(() => {
        this.updateDecoratorErrors()
      })
    }

    'function' == typeof onUpdate && onUpdate(editorState, prevEditorState)
  }

  onEditorFocus(event, editorState) {
    const { onFocus } = this.props
    'function' == typeof onFocus && onFocus(event, editorState)

    this.setActiveState(true)
  }

  onEditorBlur(event, editorState) {
    const { onBlur } = this.props
    'function' == typeof onBlur && onBlur(event, editorState)

    this.setActiveState(false)
  }

  handlePastedText(_text, _html, editorState, callback) {
    const parts = _text
      .split(/\s*,\s*/)
      .flatMap(v => v.split(/\s+/))
      .filter(v => 0 < v.trim().length)

    if (parts.every(v => Number.isInteger(Number(v)))) {
      const text = parts.join(', ')

      const content = editorState.getCurrentContent()
      const selection = editorState.getSelection()

      const newContent = Modifier.replaceText(content, selection, text)

      const lastBlock = newContent.getLastBlock()
      const selectionState = new SelectionState({
        anchorKey: lastBlock.getKey(),
        anchorOffset: lastBlock.getLength(),
        focusKey: lastBlock.getKey(),
        focusOffset: lastBlock.getLength()
      })

      const newSelection = new SelectionState({
        anchorKey: content.getLastBlock().getKey(),
        anchorOffset: 0,
        focusKey: content.getFirstBlock().getKey(),
        focusOffset: content.getLastBlock().getLength()
      })
      const newText = Modifier.removeRange(newContent, selectionState, 'backward').getPlainText('\n')
      const newEditorState = EditorState.push(editorState, Modifier.replaceText(content, newSelection, newText))

      const key = newEditorState.getCurrentContent().getLastBlock().getKey()
      const offset = Math.min(
        (selection.getIsBackward() ? selection.getFocusOffset() : selection.getAnchorOffset()) + text.length,
        newText.length
      )

      callback(
        EditorState.forceSelection(
          newEditorState,
          new SelectionState({
            anchorKey: key,
            anchorOffset: offset,
            focusKey: key,
            focusOffset: offset,
            hasFocus: true
          })
        )
      )

      return 'handled'
    }

    return 'non-handled'
  }

  onSuggestionsBeforeSelect() {
    this.pluginStore.canAutocompleteBeActive = this.canAutocompleteBeActive
  }

  setActiveState(active) {
    const { hovered } = this.state
    const { onActive, hintPosition = 'bottom' } = this.props
    const showHint = ((active || hovered) && 'top' === hintPosition) || (!active && 'top' !== hintPosition)

    'function' == typeof onActive && onActive(active)

    this.setState({ active, showHint }, () => {
      this.parserTimer && clearTimeout(this.parserTimer)

      this.updateDecoratorErrors()
      this.parserTimer = setTimeout(() => {
        this.refText.forceUpdateEditorState()
      }, 0)
    })
  }

  updateDecoratorErrors() {
    const { active, errors } = this.state
    const plugin = this.getSuggestionPlugin()

    this.decoratorErrorsStore.getErrors = active
      ? () => errors.filter(error => 'parse' !== error.get('type'))
      : () => errors

    this.decoratorErrorsStore.strategy = active
      ? plugin
        ? WarningsDecoratorStrategy(plugin.getCursor())
        : null
      : ErrorsDecoratorStrategy
  }

  updateSuggestionsComponent() {
    if (this.refText && this.refText.refEditor) {
      this.refText.refEditor.onChange(this.refText.state.editorState)
    }
  }

  calculateCursorStyles() {
    if (
      this.refText &&
      this.refText.refEditor &&
      this.refText.refEditor.editor &&
      this.refText.refEditor.editor.editor
    ) {
      const element = this.refText.refEditor.editor.editor
      const style = window.getComputedStyle(element)

      this.setState({
        cursorTextStyle: style ? style.font : null
      })
    }
  }

  componentDidMount() {
    window.addEventListener(EVENT_KEY, this.onAutocompleteUpdate)

    setTimeout(() => {
      this.calculateCursorStyles()
    })
  }

  componentWillUnmount() {
    this.parserTimer && clearTimeout(this.parserTimer)

    window.removeEventListener(EVENT_KEY, this.onAutocompleteUpdate)
  }

  componentDidUpdate(prevProps, prevState) {
    const { parserResultEof, parserResultPattern, surveySchema, pattern, active } = this.state

    if (prevProps.surveySchema != surveySchema) {
      this.setState({
        surveySchema: prevProps.surveySchema
      })
      return
    }

    this.pluginStore.getSuggestionsData = (beforeCursor, afterCursor) => {
      return {
        aytmLogic: extractAytmLogic(surveySchema),
        parserResult: extractParserResult(beforeCursor, afterCursor, parserResultPattern, parserResultEof)
      }
    }

    if (prevState.parserResultPattern != parserResultPattern || prevState.pattern != pattern) {
      this.parserTimer && clearTimeout(this.parserTimer)
      this.parserTimer = setTimeout(
        () => {
          this.updateDecoratorErrors()
          this.refText.forceUpdateEditorState()
        },
        active ? 500 : 0
      )
    }
  }

  getSuggestionPlugin() {
    return this.plugins && this.plugins[0] ? this.plugins[0] : null
  }

  renderSuggestions() {
    const plugin = this.getSuggestionPlugin()

    if (!plugin || !plugin.SuggestionsComponent) {
      return null
    }

    const SuggestionsComponent = plugin.SuggestionsComponent

    return (
      <SuggestionsComponent
        ref={ref => (this.refSuggestionsComponent = ref)}
        onBeforeSelect={this.onSuggestionsBeforeSelect}
        cursorTextStyle={this.state.cursorTextStyle}
      />
    )
  }

  renderHint(targetPosition) {
    const { locale, hintPosition = 'bottom' } = this.props
    const { showHint } = this.state

    if (targetPosition !== hintPosition) {
      return null
    }

    return (
      <div
        className={classNames({
          [style['hint']]: true,
          [style['hint--top']]: 'top' === hintPosition,
          [style['hint--bottom']]: 'bottom' === hintPosition
        })}
      >
        <div
          className={classNames({
            [style['hint-text']]: true,
            [style['hint-text--active']]: showHint
          })}
          onClick={toggleAutocomplete}
        >
          {this.canAutocompleteBeActive()
            ? locale.get('disableAutocomplete').replace('{hotkeys}', HOTKEYS)
            : locale.get('enableAutocomplete').replace('{hotkeys}', HOTKEYS)}
        </div>
      </div>
    )
  }

  render() {
    const { prefix, disabled } = this.props
    const { pattern, active, errors } = this.state
    const refErrorsCount = errors.filter(v => ['ref', 'cluster'].includes(v.get('type'))).size
    const isWarning = pattern ? 0 < errors.size && refErrorsCount == errors.size : false
    const isError = pattern ? refErrorsCount != errors.size : false
    const readOnly = true === disabled
    const withReadOnly = fn => (readOnly ? undefined : fn)

    return (
      <div
        className={style['widget']}
        onMouseEnter={withReadOnly(this.onMouseEnter)}
        onMouseLeave={withReadOnly(this.onMouseLeave)}
      >
        {this.renderHint('top')}
        <div
          className={classNames(style.container, {
            [style['container-active']]: active,
            [style['container-warning']]: !active && isWarning,
            [style['container-error']]: !active && isError,
            [style['container-disabled']]: readOnly
          })}
        >
          <div className={style.text}>
            <div className={style['text-input']} key="input">
              <div className={style['text-input-container']}>
                {prefix && <div className={style.prefix} dangerouslySetInnerHTML={{ __html: prefix }} />}
                {this.renderSuggestions()}
                <RichText
                  ref={el => (this.refText = el)}
                  html={pattern}
                  plugins={this.plugins}
                  decorators={this.decorators}
                  allowUndo={true}
                  multiline={false}
                  spellCheck={false}
                  disableFormatting={true}
                  readOnly={readOnly}
                  onChange={this.onEditorChange}
                  onUpdate={this.onEditorUpdate}
                  onFocus={withReadOnly(this.onEditorFocus)}
                  onBlur={withReadOnly(this.onEditorBlur)}
                  handlePastedText={this.handlePastedText}
                />
              </div>
            </div>
          </div>
        </div>
        {this.renderHint('bottom')}
      </div>
    )
  }
}
